import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import DOMPurify from "dompurify";
import {
  Zap,
  Plus,
  Search,
  Play,
  Pause,
  Trash2,
  ChevronRight,
  Loader2,
  Send,
  Mail,
  Users,
  Clock,
  MousePointer,
  Eye,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import toast from "react-hot-toast";
import ConfirmModal from "@/components/common/ConfirmModal";

interface Segment {
  id: string;
  name: string;
  customerCount: number;
}

interface AutomaticCampaign {
  id: string;
  name: string;
  segmentId: string;
  segmentName?: string;
  subject: string;
  isActive: boolean;
  aiPersonalize: boolean;
  sendTime: string;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  lastSentAt?: string;
  createdAt: string;
}

interface CampaignDetail extends AutomaticCampaign {
  subjectSk?: string;
  content: string;
  contentSk?: string;
  aiPrompt?: string;
  segmentPrompt?: string;
  segmentCustomerCount?: number;
  recentSends?: {
    id: string;
    customerId: string;
    customerEmail?: string;
    customerName?: string;
    subject: string;
    status: string;
    sentAt?: string;
    openedAt?: string;
    clickedAt?: string;
  }[];
}

export default function AutomationsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);

  // Fetch campaigns
  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ["automations"],
    queryFn: () => api.get<ApiResponse<AutomaticCampaign[]>>("/api/admin/automations"),
  });

  const campaigns = campaignsData?.data || [];

  // Filter campaigns
  const filteredCampaigns = campaigns.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.subject.toLowerCase().includes(search.toLowerCase()),
  );

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiResponse<{ isActive: boolean }>>(`/api/admin/automations/${id}/toggle`),
    onSuccess: () => {
      toast.success("Campaign status changed");
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: () => {
      toast.error("Failed to change campaign status");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/automations/${id}`),
    onSuccess: () => {
      toast.success("Campaign deleted");
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: () => {
      toast.error("Failed to delete campaign");
    },
  });

  // Trigger mutation
  const triggerMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<{ sentCount: number }>>(`/api/admin/automations/${id}/trigger`),
    onSuccess: (data) => {
      toast.success(`Sent ${data.data?.sentCount || 0} emails`);
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to trigger campaign");
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmTrigger, setConfirmTrigger] = useState<string | null>(null);
  const handleDelete = (id: string) => {
    setConfirmDelete(id);
  };

  const handleTrigger = (id: string) => {
    setConfirmTrigger(id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Automated campaigns</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Automatically sent emails based on segments
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-5 w-5" />
          New campaign
        </button>
      </div>

      {/* Info box */}
      <div className="border-accent-200 bg-accent-50 dark:border-accent-800 dark:bg-accent-900/20 rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <Zap className="text-accent-600 dark:text-accent-400 h-5 w-5" />
          <div>
            <h3 className="text-accent-900 dark:text-accent-100 font-medium">How does it work?</h3>
            <p className="text-accent-700 dark:text-accent-300 mt-1 text-sm">
              Create a campaign linked to a segment. Every hour, new customers in the segment are
              checked and automatically sent an email. You can enable AI personalization of content
              for each customer.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Campaigns list */}
      <div className="card">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <Zap className="mb-2 h-8 w-8 text-gray-300" />
            <p>No automated campaigns</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-primary-600 mt-2 text-sm hover:underline"
            >
              Create first campaign
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredCampaigns.map((campaign) => (
              <div key={campaign.id} className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900 dark:text-white">{campaign.name}</h3>
                      <span
                        className={`badge ${
                          campaign.isActive
                            ? "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400"
                            : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {campaign.isActive ? "Active" : "Inactive"}
                      </span>
                      {campaign.aiPersonalize && (
                        <span className="badge bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                          AI
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {campaign.subject}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {campaign.segmentName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {campaign.sendTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        {campaign.sentCount} sent
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {campaign.openedCount} opened
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="h-4 w-4" />
                        {campaign.clickedCount} clicked
                      </span>
                    </div>
                    {campaign.lastSentAt && (
                      <p className="mt-1 text-xs text-gray-500">
                        Last sent: {formatDate(campaign.lastSentAt)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTrigger(campaign.id)}
                      disabled={!campaign.isActive || triggerMutation.isPending}
                      className="btn-ghost btn-sm"
                      title="Trigger now"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleMutation.mutate(campaign.id)}
                      disabled={toggleMutation.isPending}
                      className="btn-ghost btn-sm"
                      title={campaign.isActive ? "Deactivate" : "Activate"}
                    >
                      {campaign.isActive ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedCampaign(campaign.id)}
                      className="btn-ghost btn-sm"
                      title="Details"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && <CreateCampaignModal onClose={() => setShowCreateModal(false)} />}

      {/* Detail Modal */}
      {selectedCampaign && (
        <CampaignDetailModal
          campaignId={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete);
          setConfirmDelete(null);
        }}
        isLoading={deleteMutation.isPending}
        title="Delete campaign"
        message="Are you sure you want to delete this campaign? This cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!confirmTrigger}
        onClose={() => setConfirmTrigger(null)}
        onConfirm={() => {
          if (confirmTrigger) triggerMutation.mutate(confirmTrigger);
          setConfirmTrigger(null);
        }}
        isLoading={triggerMutation.isPending}
        title="Trigger campaign now"
        message="Trigger this campaign now? Emails will be sent to new customers in the segment."
        confirmText="Trigger"
      />
    </div>
  );
}

// Create Campaign Modal
function CreateCampaignModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sendTime, setSendTime] = useState("09:00");
  const [aiPersonalize, setAiPersonalize] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  // Fetch segments
  const { data: segmentsData } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Segment[]>>("/api/admin/segments"),
  });

  const segments = segmentsData?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      segmentId: string;
      subject: string;
      content: string;
      sendTime: string;
      aiPersonalize: boolean;
      aiPrompt?: string;
    }) => api.post<ApiResponse<AutomaticCampaign>>("/api/admin/automations", data),
    onSuccess: () => {
      toast.success("Campaign created");
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create campaign");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !segmentId || !subject.trim() || !content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    createMutation.mutate({
      name,
      segmentId,
      subject,
      content,
      sendTime,
      aiPersonalize,
      aiPrompt: aiPersonalize ? aiPrompt : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">New automated campaign</h2>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label">Campaign name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome email"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Segment *</label>
            <select
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value)}
              className="input"
              required
            >
              <option value="">Select segment</option>
              {segments.map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name} ({segment.customerCount} customers)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Email content *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="HTML email content..."
              rows={6}
              className="input font-mono text-sm"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Variables: {"{{firstName}}"}, {"{{lastName}}"}, {"{{email}}"}, {"{{totalSpent}}"},{" "}
              {"{{tripsCount}}"}
            </p>
          </div>

          <div>
            <label className="label">Send time (UTC)</label>
            <select
              value={sendTime}
              onChange={(e) => setSendTime(e.target.value)}
              className="input"
            >
              {Array.from({ length: 24 }, (_, i) => {
                const hour = i.toString().padStart(2, "0");
                return (
                  <option key={hour} value={`${hour}:00`}>
                    {hour}:00
                  </option>
                );
              })}
            </select>
          </div>

          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={aiPersonalize}
                onChange={(e) => setAiPersonalize(e.target.checked)}
                className="text-primary-600 h-4 w-4 rounded border-gray-300"
              />
              <span className="font-medium text-gray-900 dark:text-white">AI personalization</span>
            </label>
            <p className="ml-7 mt-1 text-sm text-gray-500">
              AI will customize email content for each customer individually
            </p>

            {aiPersonalize && (
              <div className="mt-4">
                <label className="label">AI prompt</label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Instructions for AI on how to personalize the email..."
                  rows={3}
                  className="input"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Campaign Detail Modal
function CampaignDetailModal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const [testEmail, setTestEmail] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["automation", campaignId],
    queryFn: () => api.get<ApiResponse<CampaignDetail>>(`/api/admin/automations/${campaignId}`),
  });

  const campaign = data?.data;

  // Test send mutation
  const testMutation = useMutation({
    mutationFn: (email: string) =>
      api.post<ApiResponse<{ to: string; subject: string; content: string }>>(
        `/api/admin/automations/${campaignId}/test`,
        { email },
      ),
    onSuccess: (data) => {
      toast.success(`Test email would be sent to ${data.data?.to}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to send test email");
    },
  });

  const handleTestSend = () => {
    if (!testEmail.trim()) {
      toast.error("Enter test email");
      return;
    }
    testMutation.mutate(testEmail);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
          </div>
        ) : campaign ? (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{campaign.name}</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{campaign.subject}</p>
              </div>
              <span
                className={`badge ${
                  campaign.isActive
                    ? "bg-success-100 text-success-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {campaign.isActive ? "Active" : "Inactive"}
              </span>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-4 gap-4">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">Sent</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                  {campaign.sentCount}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">Opened</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                  {campaign.openedCount}
                </p>
                {campaign.sentCount > 0 && (
                  <p className="text-xs text-gray-500">
                    {((campaign.openedCount / campaign.sentCount) * 100).toFixed(1)}%
                  </p>
                )}
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">Clicked</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                  {campaign.clickedCount}
                </p>
                {campaign.sentCount > 0 && (
                  <p className="text-xs text-gray-500">
                    {((campaign.clickedCount / campaign.sentCount) * 100).toFixed(1)}%
                  </p>
                )}
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">Segment</p>
                <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                  {campaign.segmentCustomerCount || 0}
                </p>
                <p className="text-xs text-gray-500">customers</p>
              </div>
            </div>

            {/* Test send */}
            <div className="mt-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-white">Test send</h3>
              <div className="mt-2 flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="input flex-1"
                />
                <button
                  onClick={handleTestSend}
                  disabled={testMutation.isPending}
                  className="btn-primary"
                >
                  {testMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                  Send test
                </button>
              </div>
            </div>

            {/* Recent sends */}
            {campaign.recentSends && campaign.recentSends.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 dark:text-white">Recent sends</h3>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="pb-2 text-left font-medium text-gray-500">Email</th>
                        <th className="pb-2 text-left font-medium text-gray-500">Status</th>
                        <th className="pb-2 text-left font-medium text-gray-500">Sent</th>
                        <th className="pb-2 text-left font-medium text-gray-500">Opened</th>
                        <th className="pb-2 text-left font-medium text-gray-500">Clicked</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {campaign.recentSends.map((send) => (
                        <tr key={send.id}>
                          <td className="py-2 text-gray-900 dark:text-white">
                            {send.customerEmail}
                            {send.customerName && (
                              <span className="ml-2 text-gray-500">({send.customerName})</span>
                            )}
                          </td>
                          <td className="py-2">
                            <span
                              className={`badge ${
                                send.status === "sent"
                                  ? "bg-success-100 text-success-700"
                                  : send.status === "failed"
                                    ? "bg-error-100 text-error-700"
                                    : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {send.status}
                            </span>
                          </td>
                          <td className="py-2 text-gray-600 dark:text-gray-400">
                            {send.sentAt ? formatDate(send.sentAt) : "-"}
                          </td>
                          <td className="py-2 text-gray-600 dark:text-gray-400">
                            {send.openedAt ? formatDate(send.openedAt) : "-"}
                          </td>
                          <td className="py-2 text-gray-600 dark:text-gray-400">
                            {send.clickedAt ? formatDate(send.clickedAt) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Content preview */}
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 dark:text-white">Email content</h3>
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(campaign.content) }}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500">Campaign not found</div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-ghost">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
